import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import StatusChip from "../../components/StatusChip";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import PasswordInput from "../../components/PasswordInput";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { UsersIcon, Plus, Trash2 } from "lucide-react";

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", display_name: "", password: "", role_key: "PROJECT_MANAGER" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
    setUsers(u.data);
    setRoles(r.data);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created (must rotate password on first login)");
      setOpen(false);
      setForm({ email: "", display_name: "", password: "", role_key: "PROJECT_MANAGER" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <AppShell
      title="Users & Access"
      subtitle={`${users.length} user(s) in tenant`}
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="users-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add user
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-erf-cream" data-testid="users-add-dialog">
            <DialogHeader><DialogTitle className="font-display">Add user</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div>
                <Label className="text-xs uppercase font-nav">Email</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1 font-mono" data-testid="users-add-email" />
              </div>
              <div>
                <Label className="text-xs uppercase font-nav">Display name</Label>
                <Input required value={form.display_name} onChange={(e) => setForm({...form, display_name: e.target.value})} className="mt-1" data-testid="users-add-name" />
              </div>
              <div>
                <Label className="text-xs uppercase font-nav">Temporary password</Label>
                <Input type="password" required minLength={12} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="mt-1 font-mono hidden" data-testid="users-add-password" />
                <PasswordInput required minLength={12} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="mt-1 font-mono" testId="users-add-password-visible" autoComplete="new-password" />
                <div className="text-[11px] text-erf-dark/60 mt-1 font-nav">User will be forced to rotate on first login.</div>
              </div>
              <div>
                <Label className="text-xs uppercase font-nav">Role</Label>
                <Select value={form.role_key} onValueChange={(v) => setForm({...form, role_key: v})}>
                  <SelectTrigger className="mt-1" data-testid="users-add-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.filter(r => r.key !== "OWNER_SUPER_ADMIN").map(r => (
                      <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="users-add-submit">
                {busy ? "Creating…" : "Create user"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
        <table className="w-full erf-table">
          <thead className="bg-erf-creamSecondary">
            <tr>
              <th className="text-left px-3 py-2">Display name</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Test</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={6} className="p-0">
                <EmptyState icon={UsersIcon} title="No users yet" subtitle="Add your first user to get started." />
              </td></tr>
            )}
            {users.map((u) => (
              <tr key={u.id} className="border-t border-erf-borderCream/60" data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2 font-nav font-semibold">{u.display_name}</td>
                <td className="px-3 py-2 mono">{u.email}</td>
                <td className="px-3 py-2"><span className="font-mono text-[11px] px-1.5 py-0.5 rounded-sm border border-erf-borderCream bg-erf-creamSecondary">{u.role_key}</span></td>
                <td className="px-3 py-2">
                  {u.disabled ? <StatusChip status="error" label="Disabled" /> :
                    u.must_rotate_password ? <StatusChip status="pending" label="Awaiting rotation" /> :
                    <StatusChip status="connected" label="Active" />}
                </td>
                <td className="px-3 py-2">
                  {u.is_test_account && <StatusChip status="test" testId={`user-test-chip-${u.id}`} />}
                </td>
                <td className="px-3 py-2 text-right">
                  {u.role_key !== "OWNER_SUPER_ADMIN" && (
                    <Button size="sm" variant="ghost" className="text-red-700 h-8" onClick={() => remove(u.id)} data-testid={`user-delete-${u.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
